import { z } from 'zod';
// ---------------------------------------------------------------------------
// Patient contacts (Domain 4 - Cockpit, REQ-VIS-038/039)
// ---------------------------------------------------------------------------
/**
 * The two contact modes of the legacy `Contact.Mode` column (REQ-VIS-038):
 * `S` = SMS, `P` = phone call. The DB CHECK `patient_contacts_mode_valid`
 * enforces exactly this pair.
 */
export const PatientContactModeSchema = z.enum(['S', 'P']);
/**
 * One contact with a patient, legacy `Contact`, REQ-VIS-038/039.
 *
 * The link to a visit is OPTIONAL (`patientVisitId` nullable): a contact may
 * happen outside any visit (e.g. a general reminder), which REQ-VIS-038 allows
 * explicitly. `contactedAt` is ONE timestamp with time zone (legacy `Date_Time`)
 * - the contact's date and time are never split into two columns.
 */
export const PatientContactDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    /** `null` = the contact happened outside a visit (REQ-VIS-038). */
    patientVisitId: z.number().int().positive().nullable(),
    contactedAt: z.iso.datetime(),
    /** The person who made the contact (legacy `Personel_id`, REQ-VIS-039). */
    personnelId: z.number().int().positive(),
    mode: PatientContactModeSchema,
    /** The number the SMS went to (legacy `Phone_number`); `null` for a call. */
    phoneNumber: z.string().nullable(),
    /** SMS body or the note summarising the phone call (legacy `Text`). */
    text: z.string().min(1),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * Body of `POST /api/v1/visits/patient-contacts` (REQ-VIS-038).
 *
 * Every acceptance criterion of REQ-VIS-038 is carried here: the mode and the
 * text are required, the visit link is optional and the phone number may be
 * stored. `contactedAt` is the SERVER's `now` - the client never dictates the
 * moment of the contact (same stance as the server-side task dates). The
 * contacting person (`personnelId`) travels in the BODY: the session identity
 * carries no `personnel_details` link (ADR-006), so the dialog asks for the
 * person explicitly.
 *
 * `text` mirrors the 2000-character cap of the Domain 4 procedure comment, a
 * technical guardrail for one stored entry - not a business rule.
 */
export const CreatePatientContactRequestSchema = z.object({
    patientVisitId: z.coerce.number().int().positive().nullable().optional(),
    personnelId: z.coerce.number().int().positive(),
    mode: PatientContactModeSchema,
    phoneNumber: z.string().max(64).nullable().optional(),
    text: z.string().trim().min(1).max(2000),
});
/** Query of `GET /api/v1/visits/patient-contacts` - the contacts of one visit (REQ-VIS-038). */
export const PatientContactsQuerySchema = z.object({
    patientVisitId: z.coerce.number().int().positive(),
});
//# sourceMappingURL=patient-contacts.schema.js.map